import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Factory } from "lucide-react";
import { useIndustryList } from "@/hooks/useIndustryList";
import { track } from "@/lib/analytics";

const crossIndustryBullets = [
  "Cable lines need actual speed and energy per meter.",
  "Extrusion needs kg/hour, scrap and process stability.",
  "Machining needs cycle time, setup and work center visibility.",
  "Switchgear needs project progress, material kits and testing control.",
  "Metal fabrication needs WIP, bottleneck and rework visibility.",
  "Food and packaging needs micro-stop, batch and quality discipline.",
  "Automotive assembly needs takt, Andon and quality at source.",
  "Multi-site groups need standard KPIs across plants.",
];

// Show the 10 primary industries on the homepage (small-mid-size is a separate page)

export default function IndustryTemplates() {
  const { t } = useLanguage();
  const homepageIndustries = useIndustryList().filter((i) => i.slug !== "small-mid-size-manufacturers");
  return (
    <section id="industries" className="py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mb-12">
          <p className="eyebrow mb-3">{t("Industry templates")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t("Preconfigured for your industry. Focused on your real production losses.")}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t("BEEDIGIT is not an empty MES shell. Each industry instance includes preconfigured work centers, dashboards, loss reason trees, QR label logic, job order flows, quality checks, maintenance workflows, energy KPIs and hardware setup based on how your factory actually works.")}
          </p>
          <ul className="text-sm text-muted-foreground space-y-1.5 mt-4">
            <li>{t("• In cable manufacturing, wrong line speed standards destroy planning and costing.")}</li>
            <li>{t("• In extrusion, unstable speed creates scrap and energy waste.")}</li>
            <li>{t("• In machining, outdated cycle times hide real bottlenecks.")}</li>
            <li>{t("• In switchgear, missing components and unclear project progress delay delivery.")}</li>
            <li>{t("• In food and packaging, micro-stops and batch issues reduce line performance.")}</li>
            <li>{t("• In metal fabrication, WIP waits between operations and nobody sees the real bottleneck.")}</li>
          </ul>
          <p className="text-sm text-foreground font-medium mt-4">
            {t("BEEDIGIT unifies machines, operators, orders, materials, quality, maintenance and energy on one real-time production platform.")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {homepageIndustries.map((i) => (
            <Link
              key={i.slug}
              to={`/industries/${i.slug}`}
              onClick={() => track("industry_template_click", { slug: i.slug })}
              className="group rounded-lg border border-border bg-card p-5 hover-glow flex flex-col"
            >
              <Factory className="h-6 w-6 text-primary mb-3" />
              <h3 className="font-semibold mb-2 group-hover:text-primary">{t(i.title)}</h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-4">{t(i.short)}</p>
              <span className="mt-auto text-xs text-primary inline-flex items-center gap-1">
                {t("Explore template")} <ArrowRight className="h-3 w-3" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Button variant="outline" asChild>
            <Link to="/industries">{t("Explore Industry Solutions")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
          </Button>
        </div>

        {/* Cross-industry callout */}
        <div className="mt-16 rounded-xl border border-border bg-card/60 p-8 md:p-10">
          <p className="eyebrow mb-3">{t("One trusted factory data layer")}</p>
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            {t("Different industries. Different losses. One trusted factory data layer.")}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-3xl">
            {t("BEEDIGIT is preconfigured for how your production really works. We do not start from a blank page. We configure industry-specific work centers, job order logic, downtime reasons, QR labels, quality checks, energy KPIs, dashboards and hardware connectivity so your team can start faster and focus on business results.")}
          </p>
          <ul className="grid md:grid-cols-2 gap-2 mb-6">
            {crossIndustryBullets.map((b) => (
              <li key={b} className="text-sm flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <span>{t(b)}</span>
              </li>
            ))}
          </ul>
          <Button asChild>
            <Link to="/industries">{t("Choose Your Industry")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
          </Button>
        </div>

        {/* Final global industry CTA */}
        <div className="mt-12 rounded-xl border border-primary/40 bg-background p-8 md:p-10">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">{t("See the real losses inside your production.")}</h3>
          <p className="text-muted-foreground mb-6 max-w-3xl">
            {t("Start with one line, one bottleneck or one factory. BEEDIGIT will show actual output, downtime, speed losses, quality issues, material flow, maintenance losses and energy consumption using trusted real-time shop-floor data.")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/get-started">{t("Book Industry Visibility Assessment")} <ArrowRight className="ms-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/get-started">{t("Start with One Bottleneck")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
